import { WarningAmber, WorkspacePremiumOutlined } from '@mui/icons-material';
import { Chip, Stack, Typography } from '@mui/material';
import { GridValidRowModel } from '@mui/x-data-grid-pro';
import { IconTypography } from '@procureai/ui-mui';
import { ComponentType, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { LineItemSourcingCaseAssignments } from '../../ContractDemandAwardingWidget.hooks';
import {
  ContractDemandRequestLineItem,
  ContractDemandSourcingCase,
  ContractDemandSourcingCaseQuoteItem,
} from '../../ContractDemandAwardingWidget.types';
import { ItemSelectionCell } from '../ItemSelectionCell/ItemSelectionCell';
import { TsrctSourcingCaseQuoteItemStatus } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import {
  getBidDeclinedReasonLabel,
  getNegotiationFailedReasonLabel,
  getQuoteItemStatusLabel,
} from '~/modules/DemandAwardingWidget/cells/QuoteItemCell/QuoteItemCell.i18n';

export interface QuoteItemCellProps<R extends GridValidRowModel = any> {
  quoteItem: ContractDemandSourcingCaseQuoteItem;
  row?: R;
  lineItemAssignments: LineItemSourcingCaseAssignments;
  sourcingCase: ContractDemandSourcingCase;
  demandRequestItem: ContractDemandRequestLineItem;
}

export const SelectableQuoteItemCell = (props: QuoteItemCellProps) => {
  const { quoteItem, lineItemAssignments, sourcingCase, demandRequestItem } = props;
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'ContractDemandAwardingWidget' });

  let warning: ReactNode = undefined;
  if (quoteItem.status === TsrctSourcingCaseQuoteItemStatus.FINAL_OFFER_PROPOSED) {
    warning = (
      <Stack>
        <IconTypography startIcon={<WarningAmber color="warning" />} color="warning" variant="body2" noWrap={false}>
          {t(`cell.itemSelection.finalOfferProposed`)}
        </IconTypography>
        <NegotiationFailedQuoteItemCell {...props} />
      </Stack>
    );
  }
  let adornment: ReactNode = undefined;
  if (lineItemAssignments.hasBestOfferForQuoteItem(quoteItem.id)) {
    adornment = <Chip label={t(`cell.itemSelection.bestOffer`)} color="success" variant="outlined" />;
  }
  return (
    <Stack spacing={2} width="100%">
      <ItemSelectionCell
        checked={lineItemAssignments.isLineItemAssignedToSourcingCase(demandRequestItem.id, sourcingCase)}
        onToggle={() => lineItemAssignments.toggleLineItemAssignment(demandRequestItem.id, sourcingCase)}
        label={t(`cell.itemSelection.awardThisItem`)}
        radioStart={adornment}
      />
      {warning}
    </Stack>
  );
};

export const AwardedQuoteItemCell = ({ quoteItem }: QuoteItemCellProps) => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'ContractDemandAwardingWidget' });
  return (
    <IconTypography
      startIcon={<WorkspacePremiumOutlined color="success" />}
      color="success"
      variant="body2"
      fontWeight="bold"
    >
      {getQuoteItemStatusLabel(t, quoteItem.status)}
    </IconTypography>
  );
};

export const DeclinedQuoteItemCell = ({ quoteItem }: QuoteItemCellProps) => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'ContractDemandAwardingWidget' });
  return (
    <Typography component="div" variant="body2" color={'text.secondary'}>
      {getBidDeclinedReasonLabel(t, quoteItem.bidDeclinedReason, quoteItem.bidDeclinedReasonText)}
    </Typography>
  );
};

export const DefaultQuoteItemCell = (props: QuoteItemCellProps) => {
  const { quoteItem } = props;
  if (quoteItem.isAwardable) {
    return <SelectableQuoteItemCell {...props} />;
  }

  return <StatusQuoteItemCell {...props} />;
};

export const StatusQuoteItemCell = ({ quoteItem }: QuoteItemCellProps) => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'ContractDemandAwardingWidget' });
  return (
    <Typography component="div" variant="body2" color={'text.secondary'}>
      {getQuoteItemStatusLabel(t, quoteItem.status)}
    </Typography>
  );
};

export const NegotiationFailedQuoteItemCell = ({ quoteItem }: QuoteItemCellProps) => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'ContractDemandAwardingWidget' });
  return (
    <Typography component="div" variant="body2" color={'text.secondary'}>
      {getNegotiationFailedReasonLabel(t, quoteItem.negotiationFailedReason, quoteItem.negotiationFailedReasonText)}
    </Typography>
  );
};

const QUOTE_ITEM_CELL_STATES: Record<TsrctSourcingCaseQuoteItemStatus, ComponentType<QuoteItemCellProps>> = {
  [TsrctSourcingCaseQuoteItemStatus.REQUESTED]: DefaultQuoteItemCell,
  [TsrctSourcingCaseQuoteItemStatus.NOT_REQUESTED]: DefaultQuoteItemCell,
  [TsrctSourcingCaseQuoteItemStatus.BID_PROVIDED]: DefaultQuoteItemCell,
  [TsrctSourcingCaseQuoteItemStatus.BID_DECLINED]: DeclinedQuoteItemCell,
  [TsrctSourcingCaseQuoteItemStatus.AGREEMENT_REACHED]: DefaultQuoteItemCell,
  [TsrctSourcingCaseQuoteItemStatus.NEGOTIATION_FAILED]: NegotiationFailedQuoteItemCell,
  [TsrctSourcingCaseQuoteItemStatus.AWARDED]: AwardedQuoteItemCell,
  [TsrctSourcingCaseQuoteItemStatus.OFFER_DISMISSED]: DefaultQuoteItemCell,
  [TsrctSourcingCaseQuoteItemStatus.FINAL_OFFER_PROPOSED]: DefaultQuoteItemCell,
};

export const QuoteItemCell = <R extends GridValidRowModel = any>(props: QuoteItemCellProps<R>) => {
  const Component = QUOTE_ITEM_CELL_STATES[props.quoteItem.status];

  return <Component {...props} />;
};
